import { combineReducers } from 'redux';
import { array } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { createSelector } from 'reselect';
import { AsyncMetaState, createReducer, lt, showErrorAlert, sumNumbersAsDecimals } from '@mews/framework';
import { ApplicationState, CustomerBalance, CommanderThunkAction, AsyncAction } from '@mews-commander/types';
import { compactArray, keyBy } from '@mews/fp';
import { createFetchAction, callApiInChunks } from '@mews-commander/utils';
import { META_STATE_SUCCEEDED } from '@mews-commander/legacy-entities/src/constants';
import {
    createAsyncMetaStateSelectors,
    createBaseEntitySelectors,
    createEntityMetaReducer,
} from '@mews-commander/legacy-entities/src/ducks/utils';
import { getCurrentEnterpriseDefaultCurrencyCode } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import {
    BALANCES_FETCH_STARTED,
    BALANCES_FETCH_SUCCEEDED,
    BALANCES_FETCH_FAILED,
    BALANCES_FETCH,
} from '../actionTypes';
import { moduleSelector } from '../module';

interface ModuleState {
    data: Record<CustomerBalance['customerId'], CustomerBalance>;
    meta: AsyncMetaState;
}

const CHUNK_SIZE = 100;
export const NAME = 'balances';
export const INITIAL_STATE = { balances: [], ...META_STATE_SUCCEEDED };

const dataReducer = createReducer(
    {},
    {
        [BALANCES_FETCH_SUCCEEDED]: (state: ModuleState['data'], entities: CustomerBalance[]) => ({
            ...state,
            ...keyBy('customerId', entities),
        }),
    }
);

const metaReducer = createEntityMetaReducer({
    STARTED: BALANCES_FETCH_STARTED,
    SUCCEEDED: BALANCES_FETCH_SUCCEEDED,
    FAILED: BALANCES_FETCH_FAILED,
});
export const reducer = combineReducers({ data: dataReducer, meta: metaReducer });

const getModuleState = createSelector(moduleSelector, (state) => state[NAME] as ModuleState);
export const [getCustomerBalances, getCustomerBalancesByCustomerIds, _, getCustomerBalanceByCustomerId] =
    createBaseEntitySelectors<ApplicationState, CustomerBalance, 'data'>(getModuleState, 'data');

export const [areBalancesFetching, hasBalancesFetchingFailed] = createAsyncMetaStateSelectors(getModuleState);
export const getLoadedBalancesCustomerIds = createSelector(getCustomerBalances, Object.keys);
export const getCustomersTotalBalance = createSelector(
    getCustomerBalancesByCustomerIds,
    getCurrentEnterpriseDefaultCurrencyCode,
    (balances, defaultCurrencyCode: string) =>
        pipe(
            balances,
            array.map(({ balance }) => balance.value),
            array.reduce(0, sumNumbersAsDecimals),
            (value) => ({ value, currency: defaultCurrencyCode })
        )
);

export const fetchCustomerBalances =
    (customerIds: string[], enterpriseId: string): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const promise = array.isEmpty(customerIds)
            ? Promise.resolve()
            : callApiInChunks({
                  apiCall: repositories.customers.getBalance,
                  chunkSize: CHUNK_SIZE,
                  processChunks: array.map((chunkedIds: string[]) => ({ customerIds: chunkedIds, enterpriseId })),
              })(customerIds)
                  .then(
                      flow(
                          array.map(({ balances }) => balances),
                          compactArray,
                          array.flatten
                      )
                  )
                  .catch((error) => {
                      if (error) showErrorAlert({ description: lt('CustomerBalanceFetchErrorMessage') });
                  });
        return dispatch(createFetchAction(BALANCES_FETCH, promise));
    };
